import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import {
  getAuth,
  Auth,
  inMemoryPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";
import { initializeFirestore, Firestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, Functions } from "firebase/functions";
import { Capacitor } from '@capacitor/core';

// Validar variables de entorno requeridas
const requiredEnvVars = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Comprobar variables faltantes
const missingVars = Object.entries(requiredEnvVars)
  .filter(([_, value]) => !value)
  .map(([key]) => `VITE_FIREBASE_${key.toUpperCase()}`);

if (missingVars.length > 0) {
  throw new Error(`Missing Firebase configuration: ${missingVars.join(", ")}`);
}

// Configuración de Firebase
export const firebaseConfig = {
  apiKey: requiredEnvVars.apiKey!,
  authDomain: requiredEnvVars.authDomain!,
  projectId: requiredEnvVars.projectId!,
  storageBucket: requiredEnvVars.storageBucket!,
  messagingSenderId: requiredEnvVars.messagingSenderId!,
  appId: requiredEnvVars.appId!,
  measurementId: requiredEnvVars.measurementId!,
};

// Inicializar app principal (solo una vez)
export const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

function isNativePlatform() {
  try {
    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
  } catch {
    return false;
  }
}

function createAuthInstance(targetApp: FirebaseApp): Auth {
  if (!isNativePlatform()) return getAuth(targetApp);

  try {
    return initializeAuth(targetApp, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    });
  } catch {
    return getAuth(targetApp);
  }
}

// Auth principal
export const auth: Auth = createAuthInstance(app);

// Inicializar Firestore con configuración optimizada y caché offline moderna
export const db: Firestore = initializeFirestore(app, {
  // Forzar long polling en Android/iOS para evitar errores de red (ERR_NETWORK_CHANGED)
  experimentalForceLongPolling: isNativePlatform(),
  // Configuración adicional para evitar problemas de red en Android
  ignoreUndefinedProperties: true,
  // API moderna de caché con IndexedDB multi-pestaña (soporta recargas y pestañas concurrentes limpiamente)
  cache: !isNativePlatform() && typeof window !== 'undefined'
    ? persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      })
    : undefined
});

// Storage para subida de archivos
export const storage: FirebaseStorage = getStorage(app);

// Functions para Cloud Functions
export const functions: Functions = getFunctions(app);

// Auth secundaria (lazy) para crear usuarios sin afectar la sesión actual
let _secondaryAuth: Auth | null = null;

export function getSecondaryAuth(): Auth {
  if (_secondaryAuth) return _secondaryAuth;

  // Nombrar la app secundaria determinísticamente
  const secondaryApp: FirebaseApp =
    getApps().find((a) => a.name === "secondary") ??
    initializeApp(firebaseConfig, "secondary");

  _secondaryAuth = isNativePlatform()
    ? initializeAuth(secondaryApp, { persistence: inMemoryPersistence })
    : getAuth(secondaryApp);
  return _secondaryAuth;
}
